import { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  CalendarRange,
  Check,
  CheckCircle2,
  Circle,
  Clock3,
  History,
  Inbox,
  Lightbulb,
  Plus,
  Scale,
  Sparkles
} from "lucide-react";
import { endOfWeek, isWithinInterval, parseISO, startOfWeek } from "date-fns";
import clsx from "clsx";
import type {
  DailyFocus,
  Decision,
  Task,
  Thought,
  WeeklyDraftResult,
  WeeklyReview
} from "../types";
import { makeId, saveRecord } from "../lib/db";
import {
  addLocalDays,
  formatShortDate,
  getMondayKey,
  isReviewDue,
  isTaskDue,
  localDateKey,
  nowIso
} from "../lib/time";
import { Button, EmptyState, PageHeader } from "../components/ui";
import { AiProposalDialog } from "../components/AiProposalDialog";

export function ReviewView({
  tasks,
  thoughts,
  decisions,
  focuses,
  reviews,
  onNavigate,
  notify
}: {
  tasks: Task[];
  thoughts: Thought[];
  decisions: Decision[];
  focuses: DailyFocus[];
  reviews: WeeklyReview[];
  onNavigate: (view: "tasks" | "thinking") => void;
  notify: (message: string, tone?: "default" | "error") => void;
}) {
  const weekStart = getMondayKey();
  const existing = reviews.find((review) => review.weekStart === weekStart);
  const [results, setResults] = useState(existing?.results ?? "");
  const [blockers, setBlockers] = useState(existing?.blockers ?? "");
  const [judgmentCorrection, setJudgmentCorrection] = useState(existing?.judgmentCorrection ?? "");
  const [notDoing, setNotDoing] = useState(existing?.notDoing ?? "");
  const [nextFocusTaskIds, setNextFocusTaskIds] = useState<string[]>(
    existing?.nextFocusTaskIds ?? []
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    setResults(existing?.results ?? "");
    setBlockers(existing?.blockers ?? "");
    setJudgmentCorrection(existing?.judgmentCorrection ?? "");
    setNotDoing(existing?.notDoing ?? "");
    setNextFocusTaskIds(existing?.nextFocusTaskIds ?? []);
  }, [existing]);

  const weekInterval = {
    start: startOfWeek(new Date(), { weekStartsOn: 1 }),
    end: endOfWeek(new Date(), { weekStartsOn: 1 })
  };
  const weekFocuses = focuses.filter(
    (focus) => focus.date >= weekStart && focus.date <= addLocalDays(weekStart, 6)
  );
  const focusedTaskIds = [...new Set(weekFocuses.flatMap((focus) => focus.taskIds))];
  const focusedTasks = focusedTaskIds
    .map((id) => tasks.find((task) => task.id === id))
    .filter((task): task is Task => Boolean(task));
  const completedThisWeek = tasks.filter((task) => {
    if (!task.completedAt) return false;
    const completed = parseISO(task.completedAt);
    return isWithinInterval(completed, weekInterval);
  });
  const overdue = tasks.filter(
    (task) => !["done", "dropped"].includes(task.status) && isTaskDue(task.dueAt)
  );
  const waiting = tasks.filter((task) => task.status === "waiting");
  const inbox = tasks.filter((task) => task.status === "inbox");
  const decisionsDue = decisions.filter(
    (decision) => decision.status === "active" && isReviewDue(decision.reviewAt)
  );
  const openThoughts = thoughts.filter((thought) => thought.status === "open");
  const activeTasks = tasks.filter((task) => !["done", "dropped"].includes(task.status));
  const nextFocusTasks = nextFocusTaskIds
    .map((id) => tasks.find((task) => task.id === id))
    .filter((task): task is Task => Boolean(task));

  const reviewSource = useMemo(
    () =>
      [
        `本周：${weekStart} 至 ${addLocalDays(weekStart, 6)}`,
        `本周完成任务：${completedThisWeek.map((task) => `${task.title}（${task.outcome || "未填写预期"}）`).join("；") || "无"}`,
        `本周聚焦任务：${focusedTasks.map((task) => `${task.title}（${task.status}）`).join("；") || "无"}`,
        `等待事项：${waiting.map((task) => `${task.title}，等待${task.waitingOn || "他人"}`).join("；") || "无"}`,
        `逾期事项：${overdue.map((task) => task.title).join("；") || "无"}`,
        `收件箱：${inbox.map((task) => task.title).join("；") || "无"}`,
        `待复核决策：${decisionsDue.map((decision) => `${decision.title}：${decision.expectedOutcome}`).join("；") || "无"}`,
        `待推进思考：${openThoughts.map((thought) => thought.title).join("；") || "无"}`
      ].join("\n"),
    [
      completedThisWeek,
      decisionsDue,
      focusedTasks,
      inbox,
      openThoughts,
      overdue,
      waiting,
      weekStart
    ]
  );

  async function saveReview() {
    if (
      !results.trim() ||
      !blockers.trim() ||
      !judgmentCorrection.trim() ||
      !notDoing.trim() ||
      nextFocusTaskIds.length === 0
    ) {
      notify("请回答四项复盘问题，并选出至少一件下周聚焦任务。", "error");
      return;
    }
    const timestamp = nowIso();
    const review: WeeklyReview = {
      id: existing?.id ?? makeId(),
      kind: "weeklyReview",
      title: `${weekStart} 周复盘`,
      body: results.trim(),
      createdAt: existing?.createdAt ?? timestamp,
      updatedAt: timestamp,
      revision: existing?.revision ?? 0,
      deletedAt: existing?.deletedAt,
      weekStart,
      results: results.trim(),
      blockers: blockers.trim(),
      judgmentCorrection: judgmentCorrection.trim(),
      notDoing: notDoing.trim(),
      nextFocusTaskIds,
      completedAt: existing?.completedAt ?? timestamp
    };
    await saveRecord(review);
    notify("本周复盘已保存");
  }

  function applyAiDraft(draft: WeeklyDraftResult) {
    setResults(draft.results);
    setBlockers(draft.blockers);
    setJudgmentCorrection(draft.judgmentCorrection);
    setNotDoing(draft.notDoing);
    setAiOpen(false);
    notify("AI 草案已填入，请核对后保存");
  }

  return (
    <div className="view">
      <PageHeader
        eyebrow={`${weekStart} 至 ${addLocalDays(weekStart, 6)}`}
        title="周复盘：看清结果，再定下一周"
        description="不是写本周总结，而是检查哪些判断有效、哪些承诺没有兑现。"
        action={
          <Button type="button" onClick={() => setAiOpen(true)}>
            <Sparkles size={17} /> 生成草案
          </Button>
        }
      />

      <div className="review-metrics">
        <div>
          <span><CheckCircle2 size={17} /> 本周完成</span>
          <strong>{completedThisWeek.length}</strong>
          <small>项任务</small>
        </div>
        <div>
          <span><CalendarRange size={17} /> 今日聚焦</span>
          <strong>{weekFocuses.length}</strong>
          <small>天有记录</small>
        </div>
        <div className={clsx(overdue.length > 0 && "attention")}>
          <span><Clock3 size={17} /> 当前逾期</span>
          <strong>{overdue.length}</strong>
          <small>项待处理</small>
        </div>
        <div>
          <span><Scale size={17} /> 决策复核</span>
          <strong>{decisionsDue.length}</strong>
          <small>项到期</small>
        </div>
      </div>

      <div className="review-layout">
        <main className="review-form surface">
          <div className="section-heading">
            <div>
              <p className="eyebrow">十五分钟</p>
              <h2>本周复盘</h2>
            </div>
            {existing?.completedAt && (
              <span className="review-complete"><Check size={15} /> 已保存</span>
            )}
          </div>

          <label className="review-question">
            <span><strong>01</strong> 本周真正推进了什么结果？</span>
            <textarea
              className="textarea"
              rows={4}
              value={results}
              onChange={(event) => setResults(event.target.value)}
              placeholder="只写已经发生的结果，不把忙当成成果。"
            />
          </label>
          <label className="review-question">
            <span><strong>02</strong> 哪些事没完成，卡在哪里？</span>
            <textarea
              className="textarea"
              rows={4}
              value={blockers}
              onChange={(event) => setBlockers(event.target.value)}
              placeholder="区分外部事实、资源不足和自己的决策。"
            />
          </label>
          <label className="review-question">
            <span><strong>03</strong> 哪条判断需要修正？</span>
            <textarea
              className="textarea"
              rows={4}
              value={judgmentCorrection}
              onChange={(event) => setJudgmentCorrection(event.target.value)}
              placeholder="原先怎么判断，现在看到了什么不同证据？"
            />
          </label>
          <label className="review-question">
            <span><strong>04</strong> 下周暂时不做什么？</span>
            <textarea
              className="textarea"
              rows={3}
              value={notDoing}
              onChange={(event) => setNotDoing(event.target.value)}
              placeholder="明确放弃或延后什么，用于保护真正的重点。"
            />
          </label>

          <div className="next-focus-section">
            <div className="section-heading">
              <div>
                <p className="eyebrow">下周的起点</p>
                <h3>下周聚焦任务</h3>
              </div>
              <Button type="button" onClick={() => setPickerOpen(true)}>
                <Plus size={16} /> 选择任务
              </Button>
            </div>
            {nextFocusTasks.length === 0 ? (
              <p className="muted">从现有任务里选 1-3 项。复盘结束后，它们会成为下周的主线。</p>
            ) : (
              <div className="next-focus-list">
                {nextFocusTasks.map((task, index) => (
                  <div key={task.id}>
                    <span>{index + 1}</span>
                    <p><strong>{task.title}</strong><small>{task.outcome || "尚未填写预期结果"}</small></p>
                    <button type="button" onClick={() => setNextFocusTaskIds((ids) => ids.filter((id) => id !== task.id))}>
                      移除
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="review-save-row">
            <p>AI 草案只是起点。复盘能否成立，取决于你是否核对事实。</p>
            <Button type="button" variant="primary" onClick={() => void saveReview()}>
              保存本周复盘
            </Button>
          </div>
        </main>

        <aside className="review-side">
          <section className="surface">
            <div className="section-heading">
              <div>
                <p className="eyebrow">自动汇总</p>
                <h3>还没归位</h3>
              </div>
            </div>
            <div className="review-attention-list">
              <button type="button" onClick={() => onNavigate("tasks")}>
                <Inbox size={18} />
                <span><strong>{inbox.length}</strong> 项待办仍在收件箱</span>
                <ArrowRight size={15} />
              </button>
              <button type="button" onClick={() => onNavigate("tasks")}>
                <Clock3 size={18} />
                <span><strong>{waiting.length}</strong> 项正在等待他人</span>
                <ArrowRight size={15} />
              </button>
              <button type="button" onClick={() => onNavigate("thinking")}>
                <Lightbulb size={18} />
                <span><strong>{openThoughts.length}</strong> 条思考尚未推进</span>
                <ArrowRight size={15} />
              </button>
              <button type="button" onClick={() => onNavigate("thinking")}>
                <Scale size={18} />
                <span><strong>{decisionsDue.length}</strong> 个决策到了复核日</span>
                <ArrowRight size={15} />
              </button>
            </div>
          </section>

          <section className="surface">
            <button type="button" className="collapsible-heading" onClick={() => setShowHistory((value) => !value)}>
              <span><History size={17} /> 往期复盘</span>
              <span>{reviews.length}</span>
            </button>
            {showHistory && (
              <div className="history-list">
                {reviews.length === 0 ? (
                  <p className="muted">完成第一次周复盘后，这里会保留你的判断轨迹。</p>
                ) : (
                  reviews
                    .filter((review) => review.id !== existing?.id)
                    .sort((a, b) => b.weekStart.localeCompare(a.weekStart))
                    .map((review) => (
                      <article key={review.id}>
                        <span>{review.weekStart}</span>
                        <p>{review.results}</p>
                      </article>
                    ))
                )}
              </div>
            )}
          </section>

          {weekFocuses.length > 0 && (
            <section className="surface">
              <p className="eyebrow">本周聚焦轨迹</p>
              <div className="week-timeline">
                {weekFocuses
                  .sort((a, b) => a.date.localeCompare(b.date))
                  .map((focus) => (
                    <div key={focus.id}>
                      <span>{formatShortDate(focus.date)}</span>
                      <p>{focus.taskIds.length} 项聚焦{focus.closedAt ? " · 已收尾" : ""}</p>
                    </div>
                  ))}
              </div>
            </section>
          )}
        </aside>
      </div>

      {pickerOpen && (
        <NextFocusPicker
          tasks={activeTasks}
          selectedIds={nextFocusTaskIds}
          onChange={setNextFocusTaskIds}
          onClose={() => setPickerOpen(false)}
        />
      )}

      {aiOpen && (
        <AiProposalDialog
          action="draft_weekly_review"
          selectedText={reviewSource}
          sourceLabel={`${weekStart} 当周汇总`}
          onClose={() => setAiOpen(false)}
          onApplyWeekly={applyAiDraft}
        />
      )}

      {reviews.length === 0 && (
        <div className="review-first-cue">
          <EmptyState
            icon={<CalendarRange size={24} />}
            title="第一次复盘不追求完整"
            description="哪怕只写清一个结果、一条判断修正和下周一件要事，也比不记录更有价值。"
          />
        </div>
      )}
    </div>
  );
}

function NextFocusPicker({
  tasks,
  selectedIds,
  onChange,
  onClose
}: {
  tasks: Task[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  onClose: () => void;
}) {
  function toggle(id: string) {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((item) => item !== id));
    } else if (selectedIds.length < 3) {
      onChange([...selectedIds, id]);
    }
  }
  return (
    <div className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="modal modal-medium" role="dialog" aria-modal="true" aria-label="选择下周聚焦">
        <header className="modal-header">
          <div>
            <p className="eyebrow">下周 · 最多三件</p>
            <h2>选择下周聚焦任务</h2>
          </div>
          <button type="button" className="icon-button" onClick={onClose} aria-label="关闭">×</button>
        </header>
        <div className="modal-body">
          <div className="task-picker-list">
            {tasks.length === 0 ? (
              <EmptyState
                icon={<Inbox size={23} />}
                title="没有可选任务"
                description="先去任务池建立下一周的候选事项。"
              />
            ) : (
              tasks.map((task) => {
                const selected = selectedIds.includes(task.id);
                return (
                  <button
                    key={task.id}
                    type="button"
                    className={clsx("task-picker-row", selected && "selected")}
                    onClick={() => toggle(task.id)}
                  >
                    <span className="picker-radio">{selected ? <Check size={16} /> : <Circle size={16} />}</span>
                    <span>
                      <strong>{task.title}</strong>
                      <small>{task.area || "未分类"} · {task.outcome || "尚未写清预期结果"}</small>
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
        <footer className="modal-footer">
          <span className="ai-footer-note">已选 {selectedIds.length} 项</span>
          <Button type="button" variant="primary" onClick={onClose}>完成</Button>
        </footer>
      </section>
    </div>
  );
}
